import React, { useEffect, useState } from 'react';
import { WithStyles } from '@material-ui/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Spinner from '@material-ui/core/CircularProgress';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import { ArrowLeft } from '@material-ui/icons';
import { startOfDay } from 'date-fns';
import SearchBar from '../SearchBar';
import TablePagination from '../../components/CustomTablePagination';
import Table from '../../components/CustomTable';
import { ROWS_PER_PAGE_OPTIONS } from '../../constants/table';

import styles from './styles';
import { HeaderRowConfig } from '../../components/CustomTable/CustomTable';
import { Order } from '../../utils/table';
import { VisitReport } from '../../redux/types/visits';
import { Query } from '../../utils/query';
import useVisitsReport from './hooks/useVisitsReport.hook';
import { SetFilterPayload } from '../../redux/types/filter';
import { ExportAllParams } from '../../redux/types/exportAll';
import { MimeType } from '../../redux/types/mime';

type Props = {
  loading: boolean;
  exportAllLoading: boolean;
  visitsReport: VisitReport[];
  visitsReportTotal: number;
  loadVisitsReport: (query?: Query) => void;
  exportVisitsReport: (
    selection: Array<number | string>,
    startDate: Date,
    endDate: Date
  ) => void;
  exportAllVisitsReport: (
    path: string,
    mimeType: MimeType,
    params: ExportAllParams,
    query?: Query
  ) => void;
  printVisitsReport: (
    selection: Array<number | string>,
    startDate: Date,
    endDate: Date
  ) => void;
  setFilterState: (payload: SetFilterPayload) => void;
} & WithStyles<typeof styles> &
RouteComponentProps;

const searchBarPlaceholder: string = 'Search all visits report';

const headRows: HeaderRowConfig<VisitReport>[] = [
  {
    key: 'community',
    align: 'center',
    disablePadding: false,
    label: 'Community',
  },
  {
    key: 'visits',
    align: 'center',
    disablePadding: false,
    label: 'Total Tours',
  },
  {
    key: 'webTours',
    align: 'center',
    disablePadding: false,
    label: 'Scheduled from Web',
  },
  {
    key: 'appTours',
    align: 'center',
    disablePadding: false,
    label: 'App Tours',
  },
  {
    key: 'callTours',
    align: 'center',
    disablePadding: false,
    label: 'Call Tours',
  },
  {
    key: 'missedTours',
    align: 'center',
    disablePadding: false,
    label: 'Missed Tours',
  },

  {
    key: 'propertiesPerCustomer',
    align: 'center',
    disablePadding: false,
    label: '# of Tours/Visitor',
  },
  {
    key: 'topTourDayOfWeek',
    align: 'center',
    disablePadding: false,
    label: 'Top Tour Day of Week',
  },
  {
    key: 'topTourTime',
    align: 'center',
    disablePadding: false,
    label: 'Top Tour Time',
  },
];

const VisitsReport: React.FC<Props> = (props) => {
  const {
    classes,
    visitsReport,
    visitsReportTotal,
    loadVisitsReport,
    exportAllVisitsReport,
    exportVisitsReport,
    printVisitsReport,
    loading,
    history,
    exportAllLoading,
    setFilterState,
  } = props;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const {
    order,
    orderBy,
    page,
    search,
    rowsPerPage,
    builderId,
    startDate,
    endDate,
    setSearch,
    setOrder,
    setOrderBy,
    setBuilderId,
    setStartDate,
    setEndDate,
    setPage,
    setRowsPerPage,
    dispatchVisitsReport,
  } = useVisitsReport(loadVisitsReport);

  const [selected, setSelected] = useState<VisitReport['community'][]>([]);
  const selectedLength = selected.length;

  useEffect(() => {
    setSelected([]);
  }, [setSelected, page, rowsPerPage]);

  useEffect(
    () => () => {
      const pageNameIndex = 1;
      const pathElements = history.location.pathname.split('/');
      const pathname = pathElements[pageNameIndex];

      if (pathElements.length === 3) {
        return;
      }

      if (pathname !== 'reportVisit') {
        dispatchVisitsReport('RESET_STATE');
      }
    },
    [dispatchVisitsReport, history.location.pathname],
  );

  useEffect(() => {
    if (!startDate || !endDate) {
      const newBuilderId = searchParams.get('builderId') ?? '';
      const newStartDate = startOfDay(new Date(searchParams.get('startDate') ?? ''));
      const newEndDate = new Date(searchParams.get('endDate') ?? '');
      setBuilderId(newBuilderId);
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setFilterState({
        type: 'reports',
        name: 'builderId',
        value: newBuilderId,
      });

      setFilterState({
        type: 'reports',
        name: 'startDate',
        value: newStartDate,
      });

      setFilterState({
        type: 'reports',
        name: 'endDate',
        value: newEndDate,
      });
    }
  }, [
    builderId,
    startDate,
    endDate,
    searchParams,
    setBuilderId,
    setEndDate,
    setStartDate,
    setFilterState,
  ]);

  const handleSelect = (newSelected: any[]) => {
    setSelected(newSelected);
  };

  const exportAll = () => {
    exportAllVisitsReport(
      'visits/report',
      'text/csv',
      {},
      { startDate, endDate },
    );
  };

  const handleExport = () => {
    if (selectedLength !== 0 && startDate && endDate) {
      exportVisitsReport(selected, startDate, endDate);
    }
  };

  const handlePrint = () => {
    if (selectedLength !== 0 && startDate && endDate) {
      printVisitsReport(selected, startDate, endDate);
    }
  };

  const handleChangeOrder = (
    newOrder: Order,
    newOrderBy: keyof VisitReport,
  ) => {
    setOrder(newOrder);
    setOrderBy(newOrderBy);
    setPage(0);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', {
      currentOrder: newOrder,
      currentOrderBy: newOrderBy,
      currentPage: 0,
    });
  };

  const handleChangePage = (pageNumber: number) => {
    setPage(pageNumber);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', { currentPage: pageNumber });
  };

  const handleChangeRowsPerPage = (rowsPerPageAmount: number) => {
    setPage(0);
    setRowsPerPage(rowsPerPageAmount);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', {
      currentPage: 0,
      rowsPerPageAmount,
    });
  };

  const handleSearch = (value: string): void => {
    setSearch(value);
    setPage(0);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', {
      currentPage: 0,
      contextSearch: value,
    });
  };

  return (
    <>
      <Grid container justify="space-between" style={{ marginBottom: 25 }}>
        <Grid item>
          <Button variant="contained" onClick={() => history.push('/reports')}>
            <ArrowLeft />
            GoBack
          </Button>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs>
          <SearchBar
            defaultValue={search}
            placeholder={searchBarPlaceholder}
            onChange={handleSearch}
          />
        </Grid>
      </Grid>
      <div className={classes.contentWrapper}>
        <Toolbar>
          <div>
            <Typography variant="h5">
              Visit Report
              {selectedLength > 0 ? ` ${selectedLength} selected.` : ''}
            </Typography>
          </div>
          <div className={classes.spacer} />
          {selectedLength > 0 && (
            <div className={classes.toolbarActions}>
              <span>Selected actions: </span>
              <Button
                id="fs-export-visit"
                size="small"
                disabled={selectedLength === 0}
                onClick={handleExport}
              >
                Export
              </Button>
              <Button
                size="small"
                disabled={selectedLength === 0}
                onClick={handlePrint}
              >
                Print
              </Button>
            </div>
          )}
          {exportAllLoading ? (
            <Spinner size={20} />
          ) : (
            <Button
              id="fs-export-visits"
              size="small"
              onClick={exportAll}
              disabled={visitsReport.length === 0}
            >
              Export All
            </Button>
          )}
        </Toolbar>
        <div className={classes.tableWrapper}>
          {loading && (
            <div className={classes.spinnerWrapper}>
              <Spinner />
            </div>
          )}
          <Table<VisitReport>
            className={classes.table}
            IDKey="community"
            selected={selected}
            data={visitsReport}
            rowsPerPage={rowsPerPage}
            order={order}
            orderBy={orderBy}
            header={headRows}
            onSelect={handleSelect}
            onChangeOrder={handleChangeOrder}
          />
        </div>
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          rowsPerPage={rowsPerPage}
          currentPage={page}
          rowsCount={visitsReport.length}
          rowsTotal={visitsReportTotal}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </div>
    </>
  );
};

export default React.memo(VisitsReport);
